import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  LabelList
} from 'recharts';

import { useAuth } from '../../../../../providers/AuthContext';
import Spinner from '../../../../../components/Spinner';
import { getDataWidget, getWidgetById } from '../../../../../services/apiWidgests';
import { useDashboards } from '../../../../../providers/DashboardContext';
import { connectToProcess } from '../../../../../services/sockets/processSocket';
import { useAppContext } from '../../../../../providers/AppContext';

/* =========================
   CONSTANTES
========================= */

const labelMap = {
  disponibility: "Disponibilidad",
  efficiency: "Eficiencia",
  quality: "Calidad",
  oee: "OEE"
};

/* 🎨 Colores recomendados */
const METRIC_COLORS = {
  disponibility: "#2563EB", // azul
  efficiency: "#059669",    // verde
  quality: "#D97706",       // ámbar
  oee: null
};

/* =========================
   TOOLTIP
========================= */

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const label = payload[0]?.payload?.label;

  return (
    <div className="bg-black text-white px-3 py-2 rounded text-sm opacity-90">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => {
        const key = p.dataKey;
        return(

        <div key={i} className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: p.color }}
          />
          <span>
            {labelMap[key]}: {Number(p.value).toFixed(1)}%
          </span>
        </div>
      )})}
    </div>
  );
};

/* =========================
   NORMALIZADOR
========================= */

const normalizeGroup = (g) => ({
  label: g.group,
  disponibility: g.disponibility ?? 0,
  efficiency: g.efficiency ?? 0,
  quality: g.quality ?? 0,
  oee: g.oee ?? 0
});

/* =========================
   COMPONENTE
========================= */

const OEEChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { selectedDatarange } = useDashboards();

  const [data, setData] = useState([]);
  const [general, setGeneral] = useState(null);
  const [widgetTitle, setWidgetTitle] = useState("");
  const [config, setConfig] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [loadingInfo, setLoadingInfo] = useState(true);

  /* 🔘 métricas activas */
  const [activeMetrics, setActiveMetrics] = useState(["oee"]);

  const graphType = config?.graphType || "Line";
  const isBars = graphType === "VBars" || graphType === "HBars";
  const isHorizontal = graphType === "HBars";

  const { fixedColor = "#111827" } = config;

  const hasChartData = data && data.length > 0;

  const { addToast } = useAppContext();

  /* =========================
     FETCH INFO
  ========================= */

  const refreshWidgetInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setWidgetTitle(response?.title || "");
      setConfig(response?.configuration || {});
      setLoadingInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setLoadingInfo !== 'undefined') setLoadingInfo(false);
        if (typeof setLoadingData !== 'undefined') setLoadingData(false);
        return;
      }
      navigate('/logout');
    }
  };

  /* =========================
     FETCH DATA
  ========================= */

  const refreshWidgetData = async (silent = false) => {
    try {
      if (!silent) setLoadingData(true);

      const response = await getDataWidget(id, {}, auth?.token) || {};
      setGeneral(response.general || null);

      const groups = response.byGroup || [];
      setData(groups.map(normalizeGroup));

      if (!silent) setLoadingData(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setLoadingData !== 'undefined') setLoadingData(false);
        if (typeof setLoadingInfo !== 'undefined') setLoadingInfo(false);
        return;
      }
      navigate('/logout');
    }
  };

  /* =========================
     EFFECTS
  ========================= */

  useEffect(() => {
    refreshWidgetInfo();
    refreshWidgetData();
  }, [selectedDatarange]);

  useEffect(() => {
    if (!config?.disponibility?.process?.id) return;

    const disconnect = connectToProcess(
      config.disponibility.process.id,
      process.env.REACT_APP_SOCKET_URL,
      () => refreshWidgetData(true)
    );

    return () => disconnect();
  }, [config?.disponibility?.process?.id]);

  /* =========================
     LOADING
  ========================= */

  if (loadingData || loadingInfo) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  /* =========================
     TOGGLE MÉTRICAS
  ========================= */

  const toggleMetric = (k) => {
    setActiveMetrics(prev =>
      prev.includes(k)
        ? prev.filter(m => m !== k)
        : [...prev, k]
    );
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded flex flex-col gap-4">
      <div className="text-center font-semibold text-gray-700">
        {widgetTitle}
      </div>

      {/* ===== CARDS ===== */}
      {general && (
        <div className="flex gap-4 flex-wrap justify-center">
          {["disponibility", "efficiency", "quality"].map(k => (
            <div key={k} className="border rounded p-4 w-40 text-center">
              <div className="text-sm font-semibold">{labelMap[k]}</div>
              <div className="text-2xl font-bold">
                {general[k].toFixed(1)}%
              </div>
            </div>
          ))}
          <div
            className="rounded p-4 w-40 text-center text-white"
            style={{ backgroundColor: fixedColor }}
          >
            <div className="text-sm font-semibold">OEE</div>
            <div className="text-2xl font-bold">
              {general.oee.toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* ===== TOGGLES ===== */}
      {hasChartData && (
        <div className="flex gap-3 justify-center flex-wrap">
          {["oee", "disponibility", "efficiency", "quality"].map(k => (
            <button
              key={k}
              onClick={() => toggleMetric(k)}
              className={`px-3 py-1 rounded border text-sm font-medium transition
                ${activeMetrics.includes(k)
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-600"}
              `}
            >
              {labelMap[k]}
            </button>
          ))}
        </div>
      )}


      {/* ===== CHART ===== */}
      {hasChartData && (
        <ResponsiveContainer width="100%" height={440}>
          {isBars ? (
            <BarChart
              data={data}
              layout={isHorizontal ? "vertical" : "horizontal"}
              barGap={18}
              barCategoryGap={30}
              margin={{ top: 30, right: 40, bottom: 30, left: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              {isHorizontal ? (
                <>
                  <XAxis type="number" domain={[0, (max) => Math.max(100, max + 8)]} />
                  <YAxis type="category" dataKey="label" width={120} />
                </>
              ) : (
                <>
                  <XAxis dataKey="label" />
                  <YAxis domain={[0, 100]} />
                </>
              )}

              <Tooltip content={<CustomTooltip />} />

              {activeMetrics.map(k => (
                <Bar
                  key={k}
                  dataKey={k}
                  name={labelMap[k]}
                  barSize={k === "oee" ? 30 : 24}
                  radius={[6, 6, 0, 0]}
                  fill={k === "oee" ? fixedColor : METRIC_COLORS[k]}
                >
                  <LabelList
                    dataKey={k}
                    position={isHorizontal ? "right" : "top"}
                    formatter={(v) => `${v.toFixed(1)}%`}
                     fill="#1F2937"
                  />
                </Bar>
              ))}
            </BarChart>
          ) : (
            <LineChart
              data={data}
              margin={{ top: 50, right: 40, bottom: 20, left: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis domain={[0, 100]} padding={{ top: 35 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" />

              {activeMetrics.map(k => (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={k}
                  name={labelMap[k]}
                  stroke={k === "oee" ? fixedColor : METRIC_COLORS[k]}
                  strokeWidth={k === "oee" ? 4 : 2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 7 }}
                >
                  <LabelList
                    dataKey={k}
                    position="top"
                    fill="#1F2937"
                    formatter={(v) => `${v.toFixed(1)}%`}
                  />
                </Line>
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default OEEChartWidget;
